import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { ManufacturerService } from './service';
import { MergeService } from '../merge/service';
import { MANUFACTURERS_MERGE_CONFIG } from '../merge/config';
import { useMergeSuggestions, useMergePreview } from '../merge/hooks';
import {
  Manufacturer,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
  ManufacturerResponse,
  ManufacturersQueryParams,
  ManufacturerQueryResponse,
  ManufacturerNameVariant,
  ManufacturerNamePrecedenceStep,
  SetManufacturerPreferredNameResponse,
  ClearManufacturerPreferredNameResponse,
  ManufacturerSourcePanel,
  ManufacturerHistoryEvent,
  ManufacturerLinkCandidate,
  ManufacturerManualLinkRequest,
  ManufacturerManualUnlinkRequest,
  ManufacturerLinkActionResponse,
  ManufacturerLinkedProductsResponse,
  ManufacturerCorporateStructure,
  ManufacturerParentLinkResponse,
  CreateManufacturerStubRelativeRequest,
  CreateManufacturerStubRelativeResponse,
} from './types';

const CACHE_KEY = 'manufacturers';
const NAME_VARIANTS_CACHE_KEY = 'manufacturers-name-variants';
const NAME_PRECEDENCE_CONFIG_CACHE_KEY = 'manufacturers-name-precedence-config';
const SOURCE_DETAIL_CACHE_KEY = 'manufacturers-source-detail';
const LINKED_PRODUCTS_CACHE_KEY = 'manufacturers-linked-products';
const HISTORY_CACHE_KEY = 'manufacturers-history';
const CORPORATE_STRUCTURE_CACHE_KEY = 'manufacturers-corporate-structure';

export function useManufacturers(params?: ManufacturersQueryParams) {
  // TODO: Add proper return types
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  // Create a cache key that includes query parameters
  const cacheKey = useMemo(() => {
    return params
      ? `${CACHE_KEY}?${new URLSearchParams(params as Record<string, string>)}`
      : CACHE_KEY;
  }, [params]);

  const fetcher = useCallback(
    (signal?: AbortSignal) =>
      service.getAll(params, signal ? { signal } : undefined),
    [service, params]
  );

  return useQuery<ManufacturerQueryResponse>(cacheKey, fetcher);
}

export function useManufacturer(id: number) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Manufacturer>(`${CACHE_KEY}/${id}`, fetcher);
}

/**
 * The manufacturer modal's Names tab — every active name variant for a
 * manufacturer, ranked via Manufacturer_Name_Precedence_Config. Mirrors
 * useProductNameVariants (products/hooks.ts); refetches whenever the modal
 * opens for a different manufacturer.
 */
export function useManufacturerNameVariants(
  manufacturerId: number | undefined
) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === undefined) {
      return Promise.reject(
        new Error('useManufacturerNameVariants called with no manufacturerId')
      );
    }
    return service.getNameVariants(manufacturerId);
  }, [service, manufacturerId]);

  return useQuery<ManufacturerNameVariant[]>(
    `${NAME_VARIANTS_CACHE_KEY}/${manufacturerId}`,
    fetcher,
    { enabled: manufacturerId !== undefined }
  );
}

/**
 * The manufacturer modal's precedence-order tooltip. Mirrors
 * useNamePrecedenceConfig (products/hooks.ts).
 */
export function useManufacturerNamePrecedenceConfig() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(
    () => service.getNamePrecedenceConfig(),
    [service]
  );

  return useQuery<ManufacturerNamePrecedenceStep[]>(
    NAME_PRECEDENCE_CONFIG_CACHE_KEY,
    fetcher
  );
}

/**
 * Selecting a Names-tab option writes a Manual/short_display row at
 * precedence 1 (Sub-task 10.1 AC) — an immediate server write, unlike
 * Products' deferred-to-Save `preferred_product_name` field edit, since
 * manufacturers have no equivalent scalar field for a client-side draft to
 * live in (Sub-task 5.4 removed the raw name column write path). Success
 * invalidates both the manufacturer detail (server-recomputed
 * manufacturer_name) and its name-variants list (the new Manual row).
 */
export function useSetManufacturerPreferredName() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<
    SetManufacturerPreferredNameResponse,
    { manufacturerId: number; name: string }
  >(
    ({ manufacturerId, name }) =>
      service.setPreferredName(manufacturerId, name),
    {
      onSuccess: (_data, { manufacturerId }) => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${manufacturerId}`);
        queryCache.invalidate(`${NAME_VARIANTS_CACHE_KEY}/${manufacturerId}`);
      },
    }
  );
}

/** Clears the Manual/short_display row — falls back to the next precedence tier. */
export function useClearManufacturerPreferredName() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ClearManufacturerPreferredNameResponse, number>(
    manufacturerId => service.clearPreferredName(manufacturerId),
    {
      onSuccess: (data, manufacturerId) => {
        if (data.cleared) {
          queryCache.invalidate(CACHE_KEY);
          queryCache.invalidate(`${CACHE_KEY}/${manufacturerId}`);
          queryCache.invalidate(`${NAME_VARIANTS_CACHE_KEY}/${manufacturerId}`);
        }
      },
    }
  );
}

/**
 * The manufacturer modal's Details tab — one per-source panel per
 * registered source (issue #653, Task 10, Sub-task 10.2). Mirrors
 * useManufacturerNameVariants; refetches whenever the modal opens for a
 * different manufacturer.
 */
export function useManufacturerSourceDetail(
  manufacturerId: number | undefined
) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === undefined) {
      return Promise.reject(
        new Error('useManufacturerSourceDetail called with no manufacturerId')
      );
    }
    return service.getSourceDetail(manufacturerId);
  }, [service, manufacturerId]);

  return useQuery<ManufacturerSourcePanel[]>(
    `${SOURCE_DETAIL_CACHE_KEY}/${manufacturerId}`,
    fetcher,
    { enabled: manufacturerId !== undefined }
  );
}

/**
 * Real data for the shared LinkedProductsPanel (MFGV2-003 Sub-task 3.3,
 * wired for real data by MFGV2-004 Sub-task 4.4). Refetches whenever
 * `page` changes — the cache key includes it, mirroring how every other
 * paginated query in this file distinguishes pages.
 *
 * Stale-while-revalidate (MFGV3-002, Sub-task 2.1): `useQuery` sets
 * `status: 'loading'` (with no `data`) on every refetch, including a plain
 * page change — rendered naively, that unmounts/remounts the whole list on
 * every Next/Previous click. This hook keeps the LAST successful page's
 * response in a ref, keyed on everything except `page`, and returns it as
 * `data` while a same-key refetch is in flight (`isRefetching: true`) so
 * the caller can keep rendering the previous page's content instead of a
 * bare loading state. Switching `source`/`naturalKey` (a different list
 * entirely, not just a different page of the same one) changes the key, so
 * the stale fallback is never shown for the wrong list.
 */
export function useManufacturerLinkedProducts(
  manufacturerId: number | undefined,
  source: string,
  page: number,
  pageSize = 10,
  naturalKey?: string
) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === undefined) {
      return Promise.reject(
        new Error('useManufacturerLinkedProducts called with no manufacturerId')
      );
    }
    return service.getLinkedProducts(
      manufacturerId,
      source,
      page,
      pageSize,
      naturalKey
    );
  }, [service, manufacturerId, source, page, pageSize, naturalKey]);

  const query = useQuery<ManufacturerLinkedProductsResponse>(
    `${LINKED_PRODUCTS_CACHE_KEY}/${manufacturerId}/${source}/${page}/${pageSize}/${naturalKey ?? ''}`,
    fetcher,
    { enabled: manufacturerId !== undefined }
  );

  // Deliberately excludes `page` — this is what lets a page change reuse
  // the previous page's data as a stale fallback rather than treating
  // every page as its own "first load." `pageSize` IS included (round-6
  // Copilot finding): a pageSize change is a different result shape, not
  // just "another page," so it must invalidate the stale fallback too.
  const stableKey = `${manufacturerId}/${source}/${pageSize}/${naturalKey ?? ''}`;
  const lastSuccessRef = useRef<{
    key: string;
    data: ManufacturerLinkedProductsResponse;
  } | null>(null);

  const successData = query.status === 'success' ? query.data : undefined;

  // `query.status`/`query.data` come from the shared useQuery hook, which
  // does NOT clear its state when `key` changes -- there is exactly one
  // transitional render, right after manufacturerId/source/naturalKey/
  // pageSize changes, where `stableKey` already reflects the NEW list but
  // `query.data` still holds the PREVIOUS list's last successful response
  // (the effect that actually calls execute() for the new key hasn't run
  // yet). Without this guard, that stale `successData` would get cached
  // under the NEW `stableKey`, and the very next loading render would then
  // serve the wrong manufacturer's/source's data as this list's "stale
  // fallback" (Copilot round 10). `keyJustChanged` -- computed at render
  // time by comparing against the key as of the LAST committed render --
  // detects exactly that transitional render and skips the write; it
  // stays false across a plain page change (page is excluded from
  // `stableKey`), so the page-change stale-while-revalidate behavior this
  // hook exists for is unaffected.
  const prevStableKeyRef = useRef(stableKey);
  const keyJustChanged = prevStableKeyRef.current !== stableKey;
  useEffect(() => {
    prevStableKeyRef.current = stableKey;
  });

  // Writing this ref in an effect (not during render) avoids the write
  // "sticking" from a render React later discards/replays under concurrent
  // rendering (StrictMode double-render, a suspended/interrupted render).
  useEffect(() => {
    if (successData && !keyJustChanged) {
      lastSuccessRef.current = { key: stableKey, data: successData };
    }
  }, [successData, stableKey, keyJustChanged]);

  const staleEntry =
    query.status === 'loading' && lastSuccessRef.current?.key === stableKey
      ? lastSuccessRef.current
      : null;
  const data = query.status === 'success' ? query.data : staleEntry?.data;

  return {
    ...query,
    data,
    isRefetching: staleEntry !== null,
  };
}

/**
 * On-demand candidate search for a manual source link (MFGV2-003,
 * Sub-task 3.2) — the shared "Link to Source" UI's search box. Mirrors
 * useSearchManufacturers's plain-callback shape rather than useQuery: this
 * is fired per keystroke/debounce, not cached against a stable key.
 */
export function useSearchManufacturerLinkCandidates() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useCallback(
    async (
      source: string,
      searchTerm: string,
      manufacturerId: number
    ): Promise<ManufacturerLinkCandidate[]> => {
      const trimmed = searchTerm.trim();
      if (trimmed.length < 2) return [];
      const response = await service.searchLinkCandidates(
        source,
        trimmed,
        manufacturerId
      );
      return response.data;
    },
    [service]
  );
}

/**
 * Manual link/unlink mutations for the shared source-linking UI
 * (MFGV2-003, Sub-task 3.2). Both invalidate the Details tab's source-detail
 * query so the panel refreshes with the new/removed link immediately.
 */
export function useManualLinkManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<
    ManufacturerLinkActionResponse,
    ManufacturerManualLinkRequest
  >(request => service.manualLink(request), {
    onSuccess: (_, variables) => {
      queryCache.invalidate(
        `${SOURCE_DETAIL_CACHE_KEY}/${variables.manufacturerId}`
      );
    },
  });
}

export function useManualUnlinkManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<
    ManufacturerLinkActionResponse,
    ManufacturerManualUnlinkRequest
  >(request => service.manualUnlink(request), {
    onSuccess: (_, variables) => {
      queryCache.invalidate(
        `${SOURCE_DETAIL_CACHE_KEY}/${variables.manufacturerId}`
      );
    },
  });
}

/**
 * The manufacturer modal's History tab — the recorded name/ownership-change
 * timeline (issue #653, Task 10, Sub-task 10.6). Mirrors
 * useManufacturerSourceDetail; refetches whenever the modal opens for a
 * different manufacturer.
 */
export function useManufacturerHistory(manufacturerId: number | undefined) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === undefined) {
      return Promise.reject(
        new Error('useManufacturerHistory called with no manufacturerId')
      );
    }
    return service.getHistory(manufacturerId);
  }, [service, manufacturerId]);

  return useQuery<ManufacturerHistoryEvent[]>(
    `${HISTORY_CACHE_KEY}/${manufacturerId}`,
    fetcher,
    { enabled: manufacturerId !== undefined }
  );
}

/**
 * The manufacturer modal's Corporate Structure tab (Sub-task 14.4,
 * MFGV2-014) — current parent (name-resolved) and children list. Mirrors
 * useManufacturerHistory; refetches whenever the modal opens for a
 * different manufacturer.
 */
export function useManufacturerCorporateStructure(
  manufacturerId: number | undefined
) {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === undefined) {
      return Promise.reject(
        new Error(
          'useManufacturerCorporateStructure called with no manufacturerId'
        )
      );
    }
    return service.getCorporateStructure(manufacturerId);
  }, [service, manufacturerId]);

  return useQuery<ManufacturerCorporateStructure>(
    `${CORPORATE_STRUCTURE_CACHE_KEY}/${manufacturerId}`,
    fetcher,
    { enabled: manufacturerId !== undefined }
  );
}

/**
 * Sub-task 14.2 — set/change a manufacturer's parent. Invalidates the
 * corporate-structure views of BOTH the manufacturer and its new parent
 * (the parent's own "children" list just changed too), plus the
 * manufacturer detail (parent_manufacturer_id) and History tab (the write
 * goes through updateManufacturer(), so a new parent_link event appears).
 */
export function useSetManufacturerParent() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<
    ManufacturerParentLinkResponse,
    { manufacturerId: number; parentManufacturerId: number }
  >(
    ({ manufacturerId, parentManufacturerId }) =>
      service.setParent(manufacturerId, parentManufacturerId),
    {
      onSuccess: (_data, { manufacturerId, parentManufacturerId }) => {
        // invalidatePrefix, not invalidate -- useManufacturers() keys its
        // cache entries as `${CACHE_KEY}?${queryParams}` when any filter is
        // active (Copilot review, PR #741), so an exact-match invalidate on
        // the bare CACHE_KEY never touches those, leaving filtered/search
        // list views stale after a parent-link change.
        queryCache.invalidatePrefix(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${manufacturerId}`);
        queryCache.invalidate(
          `${CORPORATE_STRUCTURE_CACHE_KEY}/${manufacturerId}`
        );
        queryCache.invalidate(
          `${CORPORATE_STRUCTURE_CACHE_KEY}/${parentManufacturerId}`
        );
        queryCache.invalidate(`${HISTORY_CACHE_KEY}/${manufacturerId}`);
      },
    }
  );
}

/**
 * Sub-task 14.2 — remove a manufacturer's parent link. Same invalidation
 * shape as useSetManufacturerParent, but the OLD parent's id isn't known
 * client-side at call time, so its corporate-structure cache entry isn't
 * invalidated here — it self-corrects the next time that parent's own
 * panel is opened (a stale "still has this child" view for an already-open
 * OTHER modal is an acceptable, momentary staleness, not a correctness bug).
 */
export function useRemoveManufacturerParent() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ManufacturerParentLinkResponse, number>(
    manufacturerId => service.removeParent(manufacturerId),
    {
      onSuccess: (_data, manufacturerId) => {
        // invalidatePrefix -- see useSetManufacturerParent's own comment.
        queryCache.invalidatePrefix(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${manufacturerId}`);
        queryCache.invalidate(
          `${CORPORATE_STRUCTURE_CACHE_KEY}/${manufacturerId}`
        );
        queryCache.invalidate(`${HISTORY_CACHE_KEY}/${manufacturerId}`);
      },
    }
  );
}

/**
 * Sub-task 14.3 — create a stub parent/child and link it to the requesting
 * manufacturer in one call. Invalidates the requesting manufacturer's own
 * corporate-structure/detail/history views; the new stub itself has no
 * client-side cache entry yet (it didn't exist before this call), so
 * there's nothing to invalidate for it.
 */
export function useCreateManufacturerStubRelative() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<
    CreateManufacturerStubRelativeResponse,
    CreateManufacturerStubRelativeRequest
  >(request => service.createStubRelative(request), {
    onSuccess: (_data, { manufacturerId }) => {
      // invalidatePrefix -- see useSetManufacturerParent's own comment.
      queryCache.invalidatePrefix(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${manufacturerId}`);
      queryCache.invalidate(
        `${CORPORATE_STRUCTURE_CACHE_KEY}/${manufacturerId}`
      );
      queryCache.invalidate(`${HISTORY_CACHE_KEY}/${manufacturerId}`);
    },
  });
}

export function useCreateManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ManufacturerResponse, CreateManufacturerRequest>(
    data => service.create(data),
    {
      // When a new manufacturer is created, invalidate the manufacturers list
      onSuccess: () => {
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}

export function useUpdateManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<ManufacturerResponse, UpdateManufacturerRequest>(
    data => service.update(data),
    {
      // When a manufacturer is updated, invalidate both the list and the individual manufacturer
      onSuccess: data => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${data.manufacturer_id}`);
      },
    }
  );
}

export function useDeleteManufacturer() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    // When a manufacturer is deleted, invalidate the manufacturers list
    onSuccess: (_, id) => {
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${id}`);
    },
  });
}

/**
 * On-demand server-side search across ALL manufacturers, not just the
 * caller's currently-loaded page. Used by the Merge modal's target picker
 * (issue #653 Task 22) — `manufacturersData` in `ManufacturerReview.tsx` is
 * paginated + filtered by the grid's own search/filter state, so filtering
 * that array client-side (the modal's original behaviour) silently limited
 * merge targets to whatever happened to be on the current page.
 */
export function useSearchManufacturers() {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);

  return useCallback(
    async (searchTerm: string): Promise<Manufacturer[]> => {
      const trimmed = searchTerm.trim();
      if (!trimmed) return [];
      const response = await service.getAll({
        search: trimmed,
        limit: 50,
        listMode: 'lookup',
      });
      return response.data.manufacturers;
    },
    [service]
  );
}

// Convenience hook that combines all manufacturers operations
export function useManufacturersPage(params?: ManufacturersQueryParams) {
  const manufacturersResponse = useManufacturers(params);
  const createManufacturer = useCreateManufacturer();
  const updateManufacturer = useUpdateManufacturer();
  const deleteManufacturer = useDeleteManufacturer();

  return useMemo(
    () => ({
      // Query operations
      manufacturers: manufacturersResponse,

      // Mutation operations
      create: createManufacturer,
      update: updateManufacturer,
      delete: deleteManufacturer,

      // Convenience getters for common states
      data:
        manufacturersResponse.status === 'success'
          ? (manufacturersResponse.data?.manufacturers ?? [])
          : [],
      total:
        manufacturersResponse.status === 'success'
          ? (manufacturersResponse.data?.total ?? 0)
          : 0,
      isLoading: manufacturersResponse.status === 'loading',
      isError: manufacturersResponse.status === 'error',
      error:
        manufacturersResponse.status === 'error'
          ? manufacturersResponse.error
          : null,

      // Actions
      refetch: manufacturersResponse.refetch,
      invalidate: manufacturersResponse.invalidate,
    }),
    [
      manufacturersResponse,
      createManufacturer,
      updateManufacturer,
      deleteManufacturer,
    ]
  );
}

// Merge-specific hooks for Manufacturers
export function useMergeManufacturersSuggestions(
  sourceId: number | null,
  maxResults = 10
) {
  return useMergeSuggestions<Manufacturer>(
    MANUFACTURERS_MERGE_CONFIG,
    sourceId,
    maxResults
  );
}

export function useMergeManufacturersPreview(
  sourceId: number | null,
  targetId: number | null
) {
  return useMergePreview(MANUFACTURERS_MERGE_CONFIG, sourceId, targetId);
}

export function useMergeManufacturers() {
  const api = useApi();
  const mergeService = useMemo(
    () => new MergeService(api, MANUFACTURERS_MERGE_CONFIG),
    [api]
  );

  return useMutation(
    async (variables: {
      sourceId: number;
      targetId: number;
      request: {
        new_canonical_name: string;
        user_id: string;
        merge_reason?: string;
      };
    }) => {
      return mergeService.executeMerge(
        variables.sourceId,
        variables.targetId,
        variables.request
      );
    },
    {
      onSuccess: () => {
        // Invalidate manufacturers cache after successful merge
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}
